import React from 'react';
import { PolarArea } from 'react-chartjs-2';
import { Chart as ChartJS, RadialLinearScale, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(RadialLinearScale, ArcElement, Tooltip, Legend);

const PolarAreaChart = ({ originalData, labels, ch }) => {
  // console.log(originalData, labels, ch)
  const maxRadius = 2;

  const labelOrder1 = ['mind', 'soul', 'strength', 'heart'];
  const labelOrder2 = ['strategic', 'visionary', 'performance', 'collaborative'];
  // const labelOrder = ['mind', 'soul', 'strength', 'heart'];
  const starterLabels = ['mind', 'soul', 'strength', 'heart'];
  const leadershipLabels = ['strategic', 'visionary', 'performance', 'collaborative'];

  const starterColors = {
    mind: '#667F96',
    soul: '#00577D',
    strength: '#C99212',
    heart: '#B6111F',
  };

  const leadershipColors = {
    strategic: '#667F96',
    visionary: '#00577D',
    performance: '#C99212',
    collaborative: '#B6111F',
  };

  const isStarter = labels.every((label) => starterLabels.includes(label));
  const labelOrder = isStarter ? starterLabels : leadershipLabels;
  const colorMapping = isStarter ? starterColors : leadershipColors;

  const rearrangeDataAndLabels = (data, labels, order) => {
    const orderedData = [];
    const orderedLabels = [];

    order.forEach((label) => {
      const index = labels.indexOf(label);
      if (index !== -1) {
        orderedLabels.push(labels[index]);
        orderedData.push(data[index]);
      }
    });

    return { orderedLabels, orderedData };
  };
  const adjustDataForClockwiseRendering = (data) => {
    return [data[2], data[3], data[1], data[0]];
  };

  const smartRoundTo100 = (percentages) => {
    const total = percentages.reduce((a, b) => a + b, 0);
    const scaled = percentages.map(p => (p / total) * 100);
    const floored = scaled.map(p => Math.floor(p));
    let remainder = 100 - floored.reduce((a, b) => a + b, 0);

    const deltas = scaled.map((p, i) => ({ index: i, diff: p - floored[i] }));
    deltas.sort((a, b) => b.diff - a.diff);

    for (let i = 0; i < remainder; i++) {
      floored[deltas[i]?.index]++;
    }

    return floored;
  };

  // Rearrange data and labels
  const { orderedLabels, orderedData } = rearrangeDataAndLabels(originalData, labels, labelOrder);
  // const adjustedData = smartRoundTo100(orderedData);
  const adjustedData = originalData
  // console.log("orderedLabels",orderedLabels);
  // console.log("orderedData",orderedData)

  const backgroundColors = orderedLabels.map((label) => colorMapping[label]);


  // console.log("orderedLabels", orderedLabels)
  // console.log("colorMapping", colorMapping)
  // console.log("backgroundColors", backgroundColors)

  // Calculate radius based on percentage area
  const calculateRadius = (percentage) => Math.sqrt((percentage / 100) * Math.pow(maxRadius, 2));

  // console.log("labels", labels?.includes('mind'))

  const cleanedOriginalData = originalData
    ?.map(el => Number(el))
    .filter(el => !isNaN(el))
    .map(el => Number(el.toFixed(2)));

  const data = {
    labels: labels,
    datasets: [
      {
        label: 'Distribution',
        data: adjustedData.map((value) => calculateRadius(value)),
        backgroundColor: labels.includes("mind")
          ? ['#C99212', '#B6111F', '#00577D', '#667F96']
          : ['#C99212', '#B6111F', '#00577D', '#667F96'],
        borderColor: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'],
        borderWidth: 10,
        hoverBackgroundColor: labels.includes("mind")
          ? ['#C99212', '#B6111F', '#00577D', '#667F96']
          : ['#C99212', '#B6111F', '#00577D', '#667F96'],
        hoverBorderColor: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'],
        originalData: cleanedOriginalData
      },
    ],
  };

  const options = {
    scales: {
      r: {
        startAngle: 8 * (Math.PI / 180),
        grid: {
          display: false,
        },
        ticks: {
          display: false,
          max: maxRadius,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    layout: {
      padding: {
        top: 10,
        right: 10,
        bottom: 10,
        left: 10,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  const centerTextPlugin = {
    id: 'centerTextPlugin',
    afterDatasetsDraw: (chart) => {
      const { ctx, data } = chart;

      ctx.save();
      data.datasets[0].originalData?.forEach((value, index) => {
        const meta = chart.getDatasetMeta(0);
        const arc = meta.data[index];
        const centerPoint = arc.tooltipPosition();
        const text = `${value.toFixed(1)}%`;

        ctx.fillStyle = '#FFFFFF';
        ctx.font = value <= 15 ? '700 13px Lato' : '700 14px Lato';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        if (value <= 20) {

          if (index == 0) {
            centerPoint.y += -5;
            centerPoint.x += 5;
          }
          else if (index == 1) {
            centerPoint.y += 4;
            centerPoint.x += 4;
          }
          else if (index == 2) {
            centerPoint.y += 4;
            centerPoint.x += -4;
          }
          else {
            centerPoint.y += -4;
            centerPoint.x += -4;
          }
        }
        ctx.fillText(text, centerPoint.x, centerPoint.y);
      });
      ctx.restore();
    },
  };

  const cornerLabelsPlugin = {
    id: 'cornerLabelsPlugin',
    beforeDraw: (chart) => {
      const { ctx, chartArea, data } = chart;
      if (!chartArea || !data.labels) return;

      ctx.save();

      // Font and styling
      ctx.font = ch === 'mind' ? '700 24px Lato' : '700 18px Lato';
      ctx.fillStyle = '#343232';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const paddingX = ch === 'mind' ? 60 : 48; // Horizontal padding
      const paddingY = 10; // Vertical padding

      const topLeft = { x: chartArea.left + paddingX, y: chartArea.top + paddingY };
      const topRight = { x: chartArea.right - paddingX, y: chartArea.top + paddingY };
      const bottomLeft = { x: chartArea.left + paddingX, y: chartArea.bottom - paddingY };
      const bottomRight = { x: chartArea.right - paddingX, y: chartArea.bottom - paddingY };

      // Ensure there are exactly 4 labels
      const labels = data.labels.slice(0, 4);

      // Draw labels at the corners
      if (labels[0]) ctx.fillText(labels[0].charAt(0).toUpperCase() + labels[0].slice(1).toLowerCase(), topLeft.x, topLeft.y); // Top-left
      if (labels[1]) ctx.fillText(labels[1].charAt(0).toUpperCase() + labels[1].slice(1).toLowerCase(), bottomLeft.x, bottomLeft.y); // Top-right
      if (labels[2]) ctx.fillText(labels[2].charAt(0).toUpperCase() + labels[2].slice(1).toLowerCase(), topRight.x, topRight.y); // Bottom-left
      if (labels[3]) ctx.fillText(labels[3].charAt(0).toUpperCase() + labels[3].slice(1).toLowerCase(), bottomRight.x, bottomRight.y); // Bottom-right

      ctx.restore();
    },
  };

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <PolarArea data={data} options={options} plugins={[centerTextPlugin, cornerLabelsPlugin]} />
    </div>
  );
};

export default PolarAreaChart;
